import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import axios from "axios";
import GLOBALS from "../../utils/Globals";
import Footer from "../Footer/Footer";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";
import { Alert, Spinner, Button, Modal } from "react-bootstrap";
import { FaTriangleExclamation, FaWifi, FaTrash } from "react-icons/fa6";
import { MdRefresh } from "react-icons/md";

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-067893}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{nutrad}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{nutrad_portal}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{nutrad_portal}_need_to_be_licensed___{nutrad_portal}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{29_October_2025}____[v3]_[01]_MTc2MTY5NjAwMDAwMA==71e88be6064d0b1886823ed0ecdbe20c"
);

const styles = `
  /* Column resize handle styles */
  .ag-theme-material .ag-header-cell-resize::after {
    width: 2px;
    background-color: #ddd;
    transition: background-color 0.2s;
  }

  .ag-theme-material .ag-header-cell-resize:hover::after {
    background-color: #2196f3;
  }

  /* Add visual feedback when resizing */
  .ag-theme-material .ag-header-cell {
    transition: background-color 0.2s;
  }

  .ag-theme-material .ag-header-cell:hover {
    background-color: #f5f5f5;
  }
`;

function PublishingReports() {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dayFilter, setDayFilter] = useState(null); // null = All
  const [deleteModal, setDeleteModal] = useState({ show: false, reportId: null, report: null, deleting: false, deleteError: null });
  const gridRef = useRef();

  const filteredData = useMemo(() => {
    if (!dayFilter) return rowData;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - dayFilter);
    return rowData.filter(r => r.created_at && new Date(r.created_at) >= cutoff);
  }, [rowData, dayFilter]);

  const fetchPublishReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const baseURLGetPublishedReports =
        process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLGetPublishedReports
          : GLOBALS.DEV.baseURLGetPublishedReports;

      console.log('Environment:', process.env.REACT_APP_ENV);
      console.log('Base URL:', baseURLGetPublishedReports);

      const response = await axios.get(baseURLGetPublishedReports);

      // Ensure we're setting an array
      const data = response.data;
      if (Array.isArray(data)) {
        setRowData(data);
      } else if (data && typeof data === 'object') {
        // If the response is an object with a data property
        setRowData(Array.isArray(data.data) ? data.data : []);
      } else {
        // If neither, set empty array
        setRowData([]);
        console.error('Unexpected data format:', data);
      }
    } catch (err) {
      console.error("Error fetching publish reports:", err);

      // Handle different types of errors
      let errorMessage = 'Failed to load publishing reports. Please try again.';
      let errorType = 'general';

      if (err.code === 'ERR_NETWORK' || err.message.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        errorType = 'connection';
      } else if (err.code === 'ECONNREFUSED' || err.message.includes('ECONNREFUSED')) {
        errorMessage = 'The reporting service is currently unavailable. Please try again in a few moments.';
        errorType = 'connection';
      } else if (err.response?.status === 404) {
        errorMessage = 'Publishing reports service not found. Please contact support.';
        errorType = 'notfound';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error occurred while loading reports. Please try again later.';
        errorType = 'server';
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = 'Access denied. Please check your permissions or log in again.';
        errorType = 'auth';
      }

      setError({ message: errorMessage, type: errorType, originalError: err });
      setRowData([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPublishReports();
  }, [fetchPublishReports]);

  const handleDelete = useCallback((reportId, report) => {
    setDeleteModal({ show: true, reportId, report, deleting: false, deleteError: null });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setDeleteModal(prev => ({ ...prev, deleting: true, deleteError: null }));
    try {
      const baseURLDeletePublishReport =
        process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLDeletePublishReport
          : GLOBALS.DEV.baseURLDeletePublishReport;
      await axios.delete(`${baseURLDeletePublishReport}/${deleteModal.reportId}`);
      setRowData(prev => prev.filter(r => r._id !== deleteModal.reportId));
      setDeleteModal({ show: false, reportId: null, report: null, deleting: false, deleteError: null });
    } catch (err) {
      console.error('Error deleting report:', err);
      setDeleteModal(prev => ({ ...prev, deleting: false, deleteError: 'Failed to delete report. Please try again.' }));
    }
  }, [deleteModal.reportId]);

  // Column Definitions
  const columnDefs = useMemo(() => [
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 280, // Increased minimum width
      flex: 2, // Increased flex value
      autoHeight: true,
      wrapText: true,
      cellStyle: {
        'white-space': 'normal',
        'line-height': '1.5',
      },
      valueFormatter: (params) => {
        if (params.value) {
          // Create a date object from the UTC timestamp
          const utcDate = new Date(params.value);
          
          // Convert to local timezone
          const localDate = new Date(utcDate.getTime() - (utcDate.getTimezoneOffset() * 60000));
          
          return localDate.toLocaleString(navigator.language || 'en-US', {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZoneName: "short"
          });
        }
        return "";
      },
    },
    {
      field: "status",
      headerName: "Status",
      cellRenderer: (params) => {
        const status = params.value;
        return (
          <span
            style={{
              backgroundColor:
                status === "success" ? "#d4edda" :
                status === "failed" ? "#f8d7da" :
                "#fff3cd",
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            {status || ''}
          </span>
        );
      },
    },
    {
      field: "mapping_statistics.total_fields",
      headerName: "Total Fields",
      width: 120,
      valueFormatter: (params) => {
        return params.value || 0;
      },
      cellStyle: {
        textAlign: 'center',
        fontWeight: 'bold'
      }
    },
    {
      field: "mapping_statistics.mapped_fields",
      headerName: "Mapped Fields",
      width: 130,
      valueFormatter: (params) => {
        return params.value || 0;
      },
      cellStyle: {
        textAlign: 'center',
        color: '#52c41a',
        fontWeight: 'bold'
      }
    },
    {
      field: "mapping_statistics.static_fields_added",
      headerName: "Static Fields Added",
      width: 150,
      valueFormatter: (params) => {
        return params.value || 0;
      },
      cellStyle: {
        textAlign: 'center',
        color: '#1890ff',
        fontWeight: 'bold'
      }
    },
    {
      field: "mapping_statistics.computed_fields_added",
      headerName: "Computed Fields Added",
      width: 170,
      valueFormatter: (params) => {
        return params.value || 0;
      },
      cellStyle: {
        textAlign: 'center',
        color: '#fa8c16',
        fontWeight: 'bold'
      }
    },
    {
      field: "mapping_statistics.total_mapped_fields",
      headerName: "Total Mapped Fields",
      width: 160,
      valueFormatter: (params) => {
        return params.value || 0;
      },
      cellStyle: {
        textAlign: 'center',
        color: '#722ed1',
        fontWeight: 'bold'
      }
    },
    {
      field: "mapping_statistics.unmapped_fields",
      headerName: "Unmapped Fields",
      width: 140,
      valueFormatter: (params) => {
        return params.value || 0;
      },
      cellStyle: {
        textAlign: 'center',
        color: '#ff4d4f',
        fontWeight: 'bold'
      }
    },
    {
      field: "mapping_statistics.mapping_percentage",
      headerName: "Mapping Coverage %",
      width: 130,
      cellRenderer: (params) => {
        const value = params.value || 0;
        return (
          <span
            style={{
              backgroundColor:
                value >= 95 ? "#d4edda" :
                value >= 80 ? "#fff3cd" :
                value >= 60 ? "#ffeaa7" : "#f8d7da",
              color:
                value >= 95 ? "#155724" :
                value >= 80 ? "#856404" :
                value >= 60 ? "#b8860b" : "#721c24",
              padding: "4px 8px",
              borderRadius: "4px",
              display: "block",
              textAlign: "center",
              fontWeight: "bold"
            }}
          >
            {value ? `${value.toFixed(1)}%` : '0%'}
          </span>
        );
      },
    },
    {
      field: "mapping_statistics.processing_time_seconds",
      headerName: "Processing Time (s)",
      valueFormatter: (params) => {
        return params.value ? params.value.toFixed(2) : '0.00';
      },
    },
    {
      field: "error_message",
      headerName: "Error Details",
      flex: 2,
      minWidth: 200,
      autoHeight: true,
      wrapText: true,
      cellRenderer: (params) => {
        if (!params.value) return <span style={{ color: '#aaa' }}>—</span>;
        return (
          <span style={{ color: '#721c24', fontSize: '12px', whiteSpace: 'normal', lineHeight: '1.5' }}>
            {params.value}
          </span>
        );
      },
    },
    {
      headerName: "Actions",
      width: 110,
      sortable: false,
      resizable: false,
      cellRenderer: (params) => (
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleDelete(params.data._id, params.data)}
          title="Delete this report"
          style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px' }}
        >
          <FaTrash size={11} /> Delete
        </Button>
      ),
    },
  ], [handleDelete]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      sortable: true,
      resizable: true,
      suppressSizeToFit: false,
    };
  }, []);

  // Detail Cell Renderer
  const detailCellRenderer = (params) => {
    return (
      <div style={{ padding: "20px" }}>
        <h4>Publish Details</h4>
        <JsonView src={params.data.publish_data || {}} />
      </div>
    );
  };

  // Error state
  if (error) {
    return (
      <>
        <style>{styles}</style>
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-6">Publishing Reports</h1>

          <Alert variant="danger" className="d-flex align-items-center">
            <FaTriangleExclamation className="me-3" size={24} />
            <div className="flex-grow-1">
              <h5 className="alert-heading mb-2">Loading Error</h5>
              <p className="mb-2">{error.message}</p>
              {error.type === 'connection' && (
                <div className="small text-muted mb-3">
                  <FaWifi className="me-1" />
                  This usually indicates a network connectivity issue or the reporting service is temporarily unavailable.
                </div>
              )}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={fetchPublishReports}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Retrying...
                  </>
                ) : (
                  <>
                    <MdRefresh className="me-1" />
                    Try Again
                  </>
                )}
              </Button>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  // Loading state
  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-6">Publishing Reports</h1>

          <div className="text-center py-12">
            <Spinner animation="border" role="status" className="mb-3" size="lg">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="text-gray-600">Loading publishing reports...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Success state
  return (
    <>
      <style>{styles}</style>
      <div className="container mx-auto px-4 py-8">
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
          <h1 className="text-3xl font-bold mb-0">
            Publishing Reports
            <span className="text-lg font-normal text-gray-600 ml-3">
              ({filteredData.length}{dayFilter ? ` of ${rowData.length}` : ''} {rowData.length === 1 ? 'report' : 'reports'})
            </span>
          </h1>
          <div className="d-flex gap-2">
            {[null, 30, 60, 90].map(days => (
              <Button
                key={days ?? 'all'}
                variant={dayFilter === days ? 'outline-primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setDayFilter(days)}
              >
                {days ? `Last ${days} Days` : 'All'}
              </Button>
            ))}
          </div>
        </div>

        {filteredData.length === 0 ? (
          <Alert variant="info" className="text-center">
            <h5>No Publishing Reports Available</h5>
            <p className="mb-0">{dayFilter ? `No reports found in the last ${dayFilter} days.` : 'There are currently no publishing reports to display. Reports will appear here once documents are published.'}</p>
          </Alert>
        ) : (
          <div
            className="ag-theme-material"
            style={{ height: "800px", width: "100%" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              animateRows={true}
              masterDetail={true}
              detailCellRenderer={detailCellRenderer}
              detailRowHeight={400}
              pagination={true}
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
            />
          </div>
        )}
      </div>
      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteModal.show}
        onHide={() => !deleteModal.deleting && setDeleteModal({ show: false, reportId: null, report: null, deleting: false, deleteError: null })}
        centered
        backdrop={deleteModal.deleting ? 'static' : true}
      >
        <Modal.Header closeButton={!deleteModal.deleting}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#dc3545' }}>
            <FaTrash size={18} />
            Delete Publishing Report
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteModal.report && (() => {
            const r = deleteModal.report;
            const dateStr = r.created_at
              ? new Date(r.created_at).toLocaleString(navigator.language || 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
              : 'Unknown date';
            const status = r.status || 'unknown';
            const customer = r.customer || null;
            const coverage = r.mapping_statistics?.mapping_percentage;
            return (
              <div>
                <p className="mb-3" style={{ color: '#555' }}>
                  Are you sure you want to delete this report? <strong>This action cannot be undone.</strong>
                </p>
                <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '14px 16px', border: '1px solid #dee2e6' }}>
                  <div className="d-flex flex-column gap-2" style={{ fontSize: '14px' }}>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: '#888' }}>Date</span>
                      <span style={{ fontWeight: 500 }}>{dateStr}</span>
                    </div>
                    {customer && (
                      <div className="d-flex justify-content-between">
                        <span style={{ color: '#888' }}>Customer</span>
                        <span style={{ fontWeight: 500 }}>{customer}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ color: '#888' }}>Status</span>
                      <span style={{
                        backgroundColor: status === 'success' ? '#d4edda' : '#f8d7da',
                        color: status === 'success' ? '#155724' : '#721c24',
                        padding: '2px 10px',
                        borderRadius: '4px',
                        fontWeight: 500,
                        textTransform: 'capitalize',
                      }}>
                        {status}
                      </span>
                    </div>
                    {coverage != null && (
                      <div className="d-flex justify-content-between">
                        <span style={{ color: '#888' }}>Mapping Coverage</span>
                        <span style={{ fontWeight: 500 }}>{coverage.toFixed(1)}%</span>
                      </div>
                    )}
                  </div>
                </div>
                {deleteModal.deleteError && (
                  <Alert variant="danger" className="mt-3 mb-0 py-2">
                    <FaTriangleExclamation className="me-2" />
                    {deleteModal.deleteError}
                  </Alert>
                )}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setDeleteModal({ show: false, reportId: null, report: null, deleting: false, deleteError: null })}
            disabled={deleteModal.deleting}
          >
            Cancel
          </Button>
          <Button
            variant="outline-danger"
            onClick={handleConfirmDelete}
            disabled={deleteModal.deleting}
          >
            {deleteModal.deleting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <FaTrash size={13} className="me-2" />
                Delete Report
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </>
  );
}

export default PublishingReports;
